import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Activity, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import PasswordInput from "../components/common/PasswordInput";
import { useAuth } from "../contexts/AuthContext";
import { APP_NAME } from "../config/branding";
import { erpApi } from "../services/erpApi";

const emptySignup = {
  clinic_name: "",
  admin_name: "",
  email: "",
  password: "",
  phone: "",
  address: "",
  center_type: "clinic",
  scale: "small",
  city: "",
  state: "",
};

export default function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ username: "", password: "" });
  const [signupForm, setSignupForm] = useState(emptySignup);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetInfo, setResetInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const submitLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await auth.login(form);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitSignup = async (event) => {
    event.preventDefault();
    if (!signupForm.clinic_name || !signupForm.admin_name || !signupForm.email || !signupForm.password || !signupForm.address) {
      toast.error("Center name, admin name, email, password and address are required");
      return;
    }
    setLoading(true);
    try {
      await auth.signup(signupForm);
      toast.success("Center account created. You are admin now.");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (event) => {
    event.preventDefault();
    if (!forgotEmail) {
      toast.error("Enter your email ID");
      return;
    }
    setLoading(true);
    try {
      const response = await erpApi.forgotPassword({ email: forgotEmail });
      setResetInfo(response.data);
      toast.success(response.data.message || "Reset link generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell min-h-screen p-4 lg:grid lg:grid-cols-[0.92fr_1.08fr]" style={{ "--login-bg": `url(${process.env.PUBLIC_URL}/banner1.webp)` }}>
      <section className="login-visual hidden min-h-[calc(100vh-2rem)] items-end overflow-hidden rounded-2xl p-10 text-white lg:flex">
        <div className="relative z-10 max-w-lg">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
            <Activity className="h-8 w-8" />
          </div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-emerald-100">Clinic ERP & Diagnostics</p>
          <h1 className="font-manrope text-5xl font-bold">{APP_NAME}</h1>
          <p className="mt-5 max-w-md text-lg leading-8 text-white/85">
            Reception, lab, billing aur attendance ka daily workflow ek clean system me.
          </p>
          <div className="mt-8 grid gap-3 text-sm text-white/85">
            <div className="rounded-lg border border-white/15 bg-white/10 p-3 backdrop-blur-sm">Patients, appointments, prescriptions and reports</div>
            <div className="rounded-lg border border-white/15 bg-white/10 p-3 backdrop-blur-sm">Billing, expenses, payroll and clinic analytics</div>
            <div className="rounded-lg border border-white/15 bg-white/10 p-3 backdrop-blur-sm">Separate secure account for every center</div>
          </div>
        </div>
      </section>

      <section className="flex min-h-[calc(100vh-2rem)] items-center justify-center py-6 lg:py-8">
        <Card className="w-full max-w-2xl border border-white/70 bg-white/95 shadow-2xl shadow-emerald-950/10 backdrop-blur-xl">
          <CardContent className="p-6 sm:p-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-hospital-primary text-white">
                <Activity className="h-7 w-7" />
              </div>
              <div>
                <h1 className="font-manrope text-xl font-bold">{APP_NAME}</h1>
                <p className="text-sm text-hospital-text-muted">
                  {mode === "login" ? "Sign in to your center" : mode === "forgot" ? "Reset your password" : "Create your clinic or care center"}
                </p>
              </div>
            </div>

            <div className="mb-6 grid grid-cols-3 rounded-lg bg-gray-100 p-1">
              <button type="button" onClick={() => setMode("login")} className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === "login" ? "bg-white text-hospital-primary shadow-sm" : "text-hospital-text-muted"}`}>
                Sign in
              </button>
              <button type="button" onClick={() => setMode("forgot")} className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === "forgot" ? "bg-white text-hospital-primary shadow-sm" : "text-hospital-text-muted"}`}>
                Forgot
              </button>
              <button type="button" onClick={() => setMode("signup")} className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === "signup" ? "bg-white text-hospital-primary shadow-sm" : "text-hospital-text-muted"}`}>
                Create account
              </button>
            </div>

            {mode === "login" ? (
              <form onSubmit={submitLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email / Username</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoFocus />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <Button className="btn-primary w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
                <button type="button" className="w-full text-center text-sm font-medium text-hospital-primary" onClick={() => setMode("forgot")}>
                  Forgot password?
                </button>
              </form>
            ) : mode === "forgot" ? (
              <form onSubmit={submitForgot} className="space-y-4">
                <div className="space-y-2">
                  <Label>Registered Email ID</Label>
                  <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} autoFocus />
                </div>
                <Button className="btn-primary w-full" disabled={loading}>
                  {loading ? "Generating..." : "Generate Reset Link"}
                </Button>
                {resetInfo?.reset_url && (
                  <div className="rounded-lg border border-hospital-accent bg-hospital-primary-light p-3 text-sm">
                    <p className="font-semibold text-hospital-primary">Development reset link</p>
                    <p className="mt-1 break-all text-hospital-text-secondary">{window.location.origin}{resetInfo.reset_url}</p>
                    <Button type="button" variant="outline" className="mt-3 w-full" onClick={() => navigate(resetInfo.reset_url)}>
                      Open Reset Password
                    </Button>
                  </div>
                )}
                <button type="button" className="w-full text-center text-sm font-medium text-hospital-primary" onClick={() => setMode("login")}>
                  Back to sign in
                </button>
              </form>
            ) : (
              <form onSubmit={submitSignup} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Clinic / Care Center Name</Label>
                  <Input value={signupForm.clinic_name} onChange={(e) => setSignupForm({ ...signupForm, clinic_name: e.target.value })} autoFocus />
                </div>
                <div className="space-y-2">
                  <Label>Admin Name</Label>
                  <Input value={signupForm.admin_name} onChange={(e) => setSignupForm({ ...signupForm, admin_name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Email ID</Label>
                  <Input type="email" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <PasswordInput autoComplete="new-password" value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={signupForm.phone} onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Center Type</Label>
                  <Select value={signupForm.center_type} onValueChange={(value) => setSignupForm({ ...signupForm, center_type: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clinic">Clinic</SelectItem>
                      <SelectItem value="diagnostic_center">Diagnostic Center</SelectItem>
                      <SelectItem value="pathology_lab">Pathology Lab</SelectItem>
                      <SelectItem value="care_center">Care Center</SelectItem>
                      <SelectItem value="ngo_hospital">NGO Hospital</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Scale</Label>
                  <Select value={signupForm.scale} onValueChange={(value) => setSignupForm({ ...signupForm, scale: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Big / Multi Department</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input value={signupForm.city} onChange={(e) => setSignupForm({ ...signupForm, city: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input value={signupForm.state} onChange={(e) => setSignupForm({ ...signupForm, state: e.target.value })} />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Address</Label>
                  <Textarea value={signupForm.address} onChange={(e) => setSignupForm({ ...signupForm, address: e.target.value })} />
                </div>
                <div className="rounded-lg bg-hospital-primary-light p-3 text-sm text-hospital-text-secondary sm:col-span-2">
                  <Building2 className="mr-2 inline h-4 w-4 text-hospital-primary" />
                  The email used here becomes the admin login for this center.
                </div>
                <Button className="btn-primary w-full sm:col-span-2" disabled={loading}>
                  {loading ? "Creating account..." : "Create Center Admin Account"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
